<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import MessageApi from 'dashboard/api/inbox/message';

const store = useStore();

const isOpen = ref(false);
const showDeleteConfirm = ref(false);
const isDeleting = ref(false);
const position = ref({ x: 0, y: 0 });
const activeMessage = ref(null);

const quickEmojis = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const reactWithEmoji = async emoji => {
  if (!activeMessage.value) return;
  const msg = activeMessage.value;
  const conversationId = msg.conversationId || msg.conversation_id;
  const messageId = msg.id;

  try {
    await MessageApi.react(conversationId, messageId, emoji);
  } catch (err) {
    console.error('Erro ao reagir com emoji:', err);
  } finally {
    closeMenu();
  }
};

const canDelete = computed(() => {
  if (!activeMessage.value) return false;
  const msg = activeMessage.value;
  if (
    msg.contentAttributes?.deleted ||
    msg.contentAttributes?.is_deleted ||
    msg.content_attributes?.deleted ||
    msg.content_attributes?.is_deleted
  ) {
    return false;
  }

  const isOutgoing =
    msg.messageType === 1 ||
    msg.message_type === 1 ||
    msg.messageType === 'outgoing';
  const isMine =
    msg.senderType === 'User' ||
    msg.sender?.type === 'User' ||
    msg.isBotOrAgentMessage === true;

  return isOutgoing || isMine;
});

const canCopy = computed(() => {
  return Boolean(activeMessage.value?.content);
});

const formattedTime = computed(() => {
  if (!activeMessage.value?.createdAt && !activeMessage.value?.created_at) return '';
  const ts = activeMessage.value.createdAt || activeMessage.value.created_at;
  const date = new Date(typeof ts === 'number' && ts < 10000000000 ? ts * 1000 : ts);
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
});

const openMenu = data => {
  if (!data?.message) return;
  activeMessage.value = data.message;
  showDeleteConfirm.value = false;

  const menuWidth = 200;
  const menuHeight = 220;
  const pad = 12;

  let x = data.x || 0;
  let y = data.y || 0;

  if (x + menuWidth > window.innerWidth - pad) {
    x = window.innerWidth - menuWidth - pad;
  }
  if (y + menuHeight > window.innerHeight - pad) {
    y = window.innerHeight - menuHeight - pad;
  }

  position.value = {
    x: Math.max(pad, x),
    y: Math.max(pad, y),
  };

  isOpen.value = true;
};

const closeMenu = () => {
  isOpen.value = false;
  showDeleteConfirm.value = false;
  activeMessage.value = null;
};

const handleClickOutside = e => {
  if (!isOpen.value) return;
  const el = document.getElementById('whatsapp-global-context-menu');
  if (el && !el.contains(e.target)) {
    closeMenu();
  }
};

const handleKeyDown = e => {
  if (e.key === 'Escape' && isOpen.value) {
    closeMenu();
  }
};

onMounted(() => {
  emitter.on('OPEN_WHATSAPP_CONTEXT_MENU', openMenu);
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('scroll', closeMenu, true);
  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  emitter.off('OPEN_WHATSAPP_CONTEXT_MENU', openMenu);
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('scroll', closeMenu, true);
  window.removeEventListener('keydown', handleKeyDown);
});

const handleReply = () => {
  if (!activeMessage.value) return;
  const msg = activeMessage.value;
  const conversationId =
    msg.conversationId ||
    msg.conversation_id ||
    store.getters.getSelectedChat?.id ||
    window.location.pathname.match(/\/conversations\/(\d+)/)?.[1];

  LocalStorage.updateJsonStore(LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO, conversationId, msg.id);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, msg);
  closeMenu();
};

const handleCopy = async () => {
  if (!activeMessage.value?.content) return;
  try {
    await copyTextToClipboard(activeMessage.value.content);
    useAlert('Mensagem copiada para a área de transferência');
  } catch (err) {
    console.error('Erro ao copiar texto:', err);
  } finally {
    closeMenu();
  }
};

const handleDelete = async () => {
  if (!activeMessage.value || isDeleting.value) return;
  const msg = activeMessage.value;
  const conversationId =
    msg.conversationId ||
    msg.conversation_id ||
    store.getters.getSelectedChat?.id ||
    window.location.pathname.match(/\/conversations\/(\d+)/)?.[1];
  const messageId = msg.id;
  const accountId =
    store.getters.getCurrentAccountId ||
    window.location.pathname.match(/\/accounts\/(\d+)/)?.[1] ||
    1;

  if (!conversationId || !messageId) {
    useAlert('ID da conversa não localizado');
    return;
  }

  try {
    isDeleting.value = true;
    const url = `/api/v1/accounts/${accountId}/conversations/${conversationId}/messages/${messageId}`;
    const { data } = await window.axios.delete(url);

    if (data) {
      store.commit('ADD_MESSAGE', data);
      store.commit('DELETE_CONVERSATION_ATTACHMENTS', data);
    }
    useAlert('Mensagem apagada com sucesso');
  } catch (err) {
    useAlert('Erro ao apagar mensagem');
    console.error('Erro ao deletar mensagem:', err);
  } finally {
    isDeleting.value = false;
    closeMenu();
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen && activeMessage"
      id="whatsapp-global-context-menu"
      class="fixed z-[99999] min-w-[190px] max-w-[240px] bg-[#233138] border border-[#374248] rounded-xl shadow-2xl py-1.5 text-[#e9edef] text-[13px] select-none animate-fade-in"
      :style="{
        top: `${position.y}px`,
        left: `${position.x}px`,
      }"
    >
      <div class="flex flex-col">
        <!-- Barra de Reações Rápidas WhatsApp -->
        <div class="px-2 py-1.5 mb-1 bg-[#111b21] border-b border-slate-700/60 flex items-center justify-around rounded-t-xl select-none">
          <button
            v-for="emoji in quickEmojis"
            :key="emoji"
            type="button"
            class="p-1 text-base hover:scale-125 transition-transform cursor-pointer rounded-lg hover:bg-slate-700/50"
            :title="`Reagir com ${emoji}`"
            @click="reactWithEmoji(emoji)"
          >
            {{ emoji }}
          </button>
        </div>

        <!-- Horário / Info rápida -->
        <div v-if="formattedTime" class="px-3.5 py-1 text-[11px] text-slate-400 border-b border-slate-700/50 mb-1 flex items-center justify-between">
          <span>Enviada às {{ formattedTime }}</span>
          <span v-if="canDelete" class="text-emerald-400 font-mono text-[10px]">Sua mensagem</span>
        </div>

        <!-- Responder -->
        <button
          type="button"
          class="w-full px-3.5 py-2 hover:bg-[#182229] transition-colors flex items-center gap-2.5 text-left cursor-pointer"
          @click="handleReply"
        >
          <svg class="size-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h10a5 5 0 015 5v3M3 10l6-6m-6 6l6 6" />
          </svg>
          <span>Responder</span>
        </button>

        <!-- Copiar Texto -->
        <button
          v-if="canCopy"
          type="button"
          class="w-full px-3.5 py-2 hover:bg-[#182229] transition-colors flex items-center gap-2.5 text-left cursor-pointer"
          @click="handleCopy"
        >
          <svg class="size-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          <span>Copiar mensagem</span>
        </button>

        <!-- Apagar Mensagem (se for sua) -->
        <button
          v-if="canDelete"
          type="button"
          :disabled="isDeleting"
          class="w-full px-3.5 py-2 hover:bg-rose-950/40 text-rose-400 hover:text-rose-300 transition-colors flex items-center gap-2.5 text-left cursor-pointer border-t border-slate-700/50 mt-1 disabled:opacity-50"
          @click="handleDelete"
        >
          <span v-if="isDeleting" class="w-4 h-4 border-2 border-rose-400 border-t-transparent rounded-full animate-spin shrink-0" />
          <svg v-else class="size-4 text-rose-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <span>{{ isDeleting ? 'Apagando...' : 'Apagar para todos' }}</span>
        </button>
      </div>
    </div>
  </Teleport>
</template>
